import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, filter, take, timeout } from 'rxjs/operators';
import { OrganizationService } from '../../data/service/organization.service';
import { UserService } from '../../data/service/user.service';
import { ToasterMessageService } from '../../shared/toaster-message/toaster-message.service';
import { ToasterTypes } from '../enum/toaster-type.enum';
import { EntitlementsService, EntitlementState } from './entitlements.service';
import { SessionService } from './session.service';
import { SharedService } from './shared.service';

/** How long a switch waits for the new organisation's entitlements before going on regardless. */
export const ENTITLEMENTS_WAIT_MS = 15000;

/**
 * SD-1248: switching the selected organisation, as a service.
 *
 * This used to live in HeaderComponent.selected(), which also clicked the dropdown in the DOM, so
 * nothing else could switch organisation -- and the All clients page has to, before it sends an
 * advisor to a client's page. It does what selected() did, with three changes of order that the
 * next page depends on:
 *
 *  1. UpdateUserLastLogin and InsertUpdateUserLastLoggedOrganization are awaited. They move the
 *     user's current organisation on the server, and the role read below raced them.
 *  2. SessionService is refreshed (its cached role was the previous organisation's).
 *  3. It resolves only once EntitlementsService.state$ has emitted for the new organisation, so
 *     ModuleGuard judges the caller's next navigation against the right set of modules.
 *
 * The caller navigates afterwards. Things a switch should set off wherever it was made from (the
 * connection and document-limit notices, the Stripe sync) follow `switched$`.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationSwitchService {

  private _switched$ = new Subject<string>();

  /** Emits the organisation id once a switch has completed. */
  readonly switched$: Observable<string> = this._switched$.asObservable();

  constructor(
    private organizationService: OrganizationService,
    private userService: UserService,
    private session: SessionService,
    private entitlements: EntitlementsService,
    private sharedService: SharedService,
    private toaster: ToasterMessageService
  ) { }

  /**
   * Makes `organizationId` the selected organisation. When it is already in the organisation list
   * the selection is applied synchronously, before the first await, so a caller can rely on
   * OrganizationService reading the new organisation as soon as this returns its promise.
   */
  async switchTo(organizationId: string, name?: string): Promise<void> {
    let organization = this.find(organizationId);
    if (!organization) {
      // A client the practice has only just created or linked is not in the list the layout
      // loaded at sign-in; just created, it can take a moment to appear in it.
      await this.reloadOrganizations();
      organization = this.find(organizationId);
      if (!organization) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        await this.reloadOrganizations();
        organization = this.find(organizationId);
      }
    }

    this.applySelection(organizationId, organization, name);

    // Two paths at once: they were four requests one after another, which held a click in the
    // organisation menu for seconds. The role is read from the user's current organisation on the
    // server, so it still waits for that to move; the entitlements name the organisation in their
    // request (Modules/Me?organizationId=), so they need not.
    await Promise.all([
      this.recordLastLoggedIn(organizationId).then(() => {
        this.session.refresh();
        return this.rereadRole();
      }),
      this.reloadEntitlements()
    ]);

    this._switched$.next(organizationId);
  }

  // ── Steps ────────────────────────────────────────────────────────────────────────────────

  private find(organizationId: string): any {
    const list: any[] = this.organizationService.organizationList.value || [];
    const id = (organizationId || '').toLowerCase();
    return list.find(x => (x.organisationID || '').toLowerCase() === id);
  }

  /** The OrganizationService fields and storage, exactly as HeaderComponent.selected() set them. */
  private applySelection(organizationId: string, organization: any, name?: string): void {
    const service = this.organizationService;
    if (!organization && name) {
      // Not in the list yet: at least the header names the organisation now selected, not the last one.
      service.selectedOrganizationName = name;
    }

    if (organization) {
      organization.orderRank += 1;
      service.selectedOrganizationName = organization.organisationName;
      service.selectedOrganization = organization.organisationID;
      service.selectedOrganizationDateFormat = organization.dateFormat;
      service.selectedOrganizationDomen = organization.accountingIntegration == 0 ? 'Xero' : 'QBO';
      service.refreshConnectionURL = organization.refreshConnectionURL;
      service.baseURL = organization.baseURL;
      service.connectURL = organization.connectURL;

      // The selected organisation goes to the top of the list.
      const list: any[] = service.organizationList.value || [];
      service.organizationList.next([
        organization,
        ...list.filter(x => x.organisationID !== organization.organisationID)
      ]);
    }

    sessionStorage.organizationId = organizationId;
    sessionStorage.selectedOrganization = organizationId;
    sessionStorage.selectedOrganizationName = service.selectedOrganizationName;
    service.getSelectedOrganization(service.selectedOrganization);
  }

  /** UpdateUserLastLogin and InsertUpdateUserLastLoggedOrganization, both awaited. */
  private recordLastLoggedIn(organizationId: string): Promise<any> {
    const user: any = this.userService.loggedUserDetails.value || {};
    const id = user.id || localStorage.getItem('Id') || sessionStorage.Id;
    const userId = user.userId || localStorage.getItem('UserId') || sessionStorage.UserId;

    const lastLogin = this.userService.updateUserLastLogin({ organizationId, userId: id }).toPromise()
      .catch(err => {
        this.toaster.displayToasterState(ToasterTypes.Error, 'Error', err && err.statusText);
      });
    const lastLogged = this.organizationService.InsertUpdateUserLastLoggedOrganization(userId, organizationId)
      .toPromise()
      .catch(() => { /* as before: not worth interrupting the switch for */ });

    return Promise.all([lastLogin, lastLogged]);
  }

  /** The role in the new organisation, for the code that still reads UserService's copy. */
  private rereadRole(): Promise<void> {
    return this.userService.getUserGetByIdServer().pipe(take(1)).toPromise()
      .then(response => {
        if (response && response.data) {
          this.userService.setUserRoleData(response.data.role);
        }
      })
      .catch(() => { /* SessionService has its own copy; the sidebar reads that */ });
  }

  /**
   * Reloads the sidebar (which refreshes the entitlements) and waits for the set that answers for
   * the new organisation. state$ replays the last set straight away, so the one cached before the
   * reload is skipped; switchMap inside EntitlementsService drops any fetch still in flight for
   * the old organisation.
   */
  private reloadEntitlements(): Promise<void> {
    let previous: EntitlementState | undefined;
    this.entitlements.state$.pipe(take(1)).subscribe(state => previous = state).unsubscribe();

    const fresh = this.entitlements.state$.pipe(
      filter(state => state !== previous),
      take(1),
      timeout(ENTITLEMENTS_WAIT_MS),
      catchError(() => of(null))
    ).toPromise();

    this.sharedService.triggerSidebarReload();

    return fresh.then(() => undefined);
  }

  private reloadOrganizations(): Promise<void> {
    return this.organizationService.getOrganization('Organisation/OrganisationGet').toPromise()
      .then((response: any) => {
        if (response && response.status === 1 && response.data) {
          this.organizationService.organizationList.next(response.data);
        }
      })
      .catch(() => { /* switch with what we have */ });
  }
}
